import { Controller } from "@hotwired/stimulus";

import Modal from "src/admin/modal";

import { VisibleStore, visibleStoresResponseDecoder } from "src/admin/dispatch/model";
import { adminDispatchDashboardStoresPath, createNoteAdminDispatchDashboardStorePath } from 'src/routes';

const SELECT_ID = "shiftReportRestaurantSelect";
const TEXTAREA_ID = "shiftReportTextarea";

export default class extends Controller {
  static targets = ["dropdown"];

  declare dropdownTarget: HTMLElement;
  declare hasDropdownTarget: boolean;

  openRestaurantModal() : void {
    this.toggleDropdown();

    const content = this.restaurantModalContent();

    void this.loadStores()
      .then((stores) => this.showStores(content, stores))
      .catch(() => this.showStoreMessage(content, "Couldn't load the store list."));

    Modal.show({
      title: "Add store note",
      content: content,
      focus: "textarea",
      onAccept: async (rootEl: HTMLElement) => await this.submitRestaurantModal(rootEl),
    }).catch(() => undefined);
  }

  async submitRestaurantModal(rootEl: HTMLElement) : Promise<boolean | void> {
    const note = rootEl.querySelector(`#${TEXTAREA_ID}`) as HTMLTextAreaElement;
    const restaurantId = rootEl.querySelector(`#${SELECT_ID}`) as HTMLSelectElement;

    if (restaurantId.value === "") return false;

    const metaElement = document.querySelector("meta[name='csrf-token']") as HTMLMetaElement;
    if (metaElement === undefined) {
      throw "Missing CSRF meta tag";
    }
    const csrfToken = metaElement.content;

    const id = this.data.get("dashboard-id");
    if (id != undefined) {
      await fetch(createNoteAdminDispatchDashboardStorePath(id, restaurantId.value), {
        method: "POST",
        body: JSON.stringify({ note: note.value }),
        credentials: "include",
        headers: { "X-CSRF-Token": csrfToken, "Content-Type": "application/json" }
      });
    }

  }

  toggleDropdown() : void {
    if (this.hasDropdownTarget)
      this.dropdownTarget.classList.toggle("is-active");
  }

  private async loadStores() : Promise<VisibleStore[]> {
    const id = this.data.get("dashboard-id");
    if (id == null) throw new Error("Missing dashboard id");

    const response = await fetch(adminDispatchDashboardStoresPath(id), {
      method: "GET",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    if (response.status !== 200) throw new Error("HTTP error");

    const decoded = visibleStoresResponseDecoder.decode(await response.json());
    if (!decoded.isOk()) throw new Error(decoded.error);

    return decoded.value.stores;
  }

  private showStores(rootEl: HTMLElement, stores: VisibleStore[]) : void {
    if (stores.length === 0) {
      this.showStoreMessage(rootEl, "There are no stores in this area.");
      return;
    }

    const select = rootEl.querySelector(`#${SELECT_ID}`) as HTMLSelectElement;
    const placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.text = "Select a store...";
    placeholder.selected = true;
    placeholder.disabled = true;
    select.replaceChildren(placeholder);

    for (const store of [...stores].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))) {
      const option = document.createElement("option");
      option.text = store.name;
      option.value = String(store.storeId);
      select.add(option);
    }

    select.disabled = false;
  }

  private showStoreMessage(rootEl: HTMLElement, message: string) : void {
    const select = rootEl.querySelector(`#${SELECT_ID}`) as HTMLSelectElement;
    select.replaceChildren();
    select.disabled = true;

    const help = document.createElement("p");
    help.classList.add("help", "has-text-danger");
    help.textContent = message;

    select.closest(".field")?.appendChild(help);
  }

  private restaurantModalContent() : HTMLElement {
    const rootEl = document.createElement("div");

    const noteField = document.createElement("div");
    noteField.classList.add("field")

    const noteControl = document.createElement("div");
    noteControl.classList.add("control");

    const noteLabel = document.createElement("label");
    noteLabel.classList.add("label");
    noteLabel.innerText = "Note";

    const textarea = document.createElement("textarea");
    textarea.id = TEXTAREA_ID;
    textarea.classList.add("textarea");
    textarea.setAttribute("data-form-validator-target", "requiredField");
    textarea.placeholder = "Note about what happened for the shift report - e.g. 'Store was consistently running late all night'"

    noteControl.appendChild(noteLabel);
    noteControl.appendChild(textarea);
    noteField.appendChild(noteControl);
    rootEl.appendChild(noteField);

    const selectField = document.createElement("div");
    selectField.classList.add("field");

    const selectControl = document.createElement("div");
    selectControl.classList.add("control");

    const selectWrapper = document.createElement("div");
    selectWrapper.classList.add("select");

    const selectLabel = document.createElement("label");
    selectLabel.classList.add("label");
    selectLabel.innerText = "Store";

    const select = document.createElement("select");
    select.id = SELECT_ID;
    select.setAttribute("data-form-validator-target", "requiredField")
    select.disabled = true;

    const loading = document.createElement("option");
    loading.text = "Loading stores…";
    loading.value = "";
    select.add(loading);

    selectWrapper.appendChild(select);
    selectControl.appendChild(selectLabel);
    selectControl.appendChild(selectWrapper);
    selectField.appendChild(selectControl);
    rootEl.appendChild(selectField);

    return rootEl;
  }
}
